import React, { useState, useMemo } from "react";
import useSWR from "swr";
import { fetchScenario } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { fmtMoney, fmtPct } from "@/lib/format";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend } from "recharts";
import { Plus, X, Loader2 } from "lucide-react";

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6"];

function ScenarioLoader({ id, children }) {
  const { data } = useSWR(["scenario", id], () => fetchScenario(id));
  return children(data);
}

function Stat({ label, value, tone }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`num text-sm font-semibold ${tone || ""}`}>{value}</div>
    </div>
  );
}

function CompareCard({ scenario, index, data, baseline, onRemove, canRemove }) {
  const color = COLORS[index % COLORS.length];
  if (!data) {
    return (
      <div className="rounded-xl border border-dashed border-border p-6 flex items-center justify-center h-56">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }
  const t = data.totals;
  const delta = baseline ? t.grand_total - baseline.totals.grand_total : 0;
  const deltaPct = baseline && baseline.totals.grand_total ? (delta / baseline.totals.grand_total * 100) : 0;
  const tone = delta < 0 ? "text-emerald-600" : delta > 0 ? "text-red-600" : "text-muted-foreground";

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid={`compare-card-${scenario.id}`}>
      <div className="border-b border-border p-4 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: color }} />
            <h3 className="font-heading font-bold truncate">{scenario.name}</h3>
          </div>
          <p className="text-xs text-muted-foreground truncate">{scenario.description || "—"}</p>
        </div>
        {canRemove && (
          <Button variant="ghost" size="sm" onClick={onRemove} className="h-7 w-7 p-0" data-testid={`compare-remove-${scenario.id}`}>
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      <div className="p-4 space-y-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Grand Total</div>
          <div className="num text-2xl font-bold" style={{ color }}>{fmtMoney(t.grand_total)}</div>
        </div>
        {baseline && baseline.scenario.id !== scenario.id && (
          <div className={`text-sm ${tone}`}>
            {delta > 0 ? "+" : ""}{fmtMoney(delta)} <span className="text-xs opacity-70">({deltaPct > 0 ? "+" : ""}{deltaPct.toFixed(2)}%)</span>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border/60">
          <Stat label="Subtotal" value={fmtMoney(t.revised_subtotal)} />
          <Stat label="VAT" value={fmtMoney(t.vat_amount)} />
          <Stat label="Markup" value={fmtPct(data.params.global_markup_pct, 1)} />
          <Stat label="Discount" value={fmtPct(data.params.global_discount_pct, 1)} />
          <Stat label="Included" value={t.included_count} />
          <Stat label="Excluded" value={t.excluded_count} tone={t.excluded_count > 0 ? "text-amber-600" : ""} />
        </div>
      </div>
    </div>
  );
}

function CompareChart({ selected, scenarios, dataById }) {
  const chartData = useMemo(() => {
    if (!selected.every(id => dataById[id])) return [];
    const map = new Map();
    selected.forEach(id => {
      const s = dataById[id];
      s.sections.forEach(sec => {
        const row = map.get(sec.section_no) || {
          section: sec.section_title.replace(/\s+WORKS?/i, "").replace(/HANDLING AND COORDINATION FEE/i, "Handling").replace(/PRELIMINARIES AND GENERAL REQUIREMENTS/i, "Prelims"),
        };
        row[s.scenario.name] = sec.revised;
        map.set(sec.section_no, row);
      });
    });
    return Array.from(map.values());
  }, [selected, dataById]);

  if (chartData.length === 0) return null;

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="compare-chart">
      <h3 className="font-heading text-lg font-bold mb-4">Section-level Comparison</h3>
      <div className="h-[420px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ left: 10, right: 20 }}>
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
            <XAxis dataKey="section" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} angle={-15} textAnchor="end" height={70} interval={0} />
            <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickFormatter={(v) => `${Math.round(v/1000)}k`} />
            <RTooltip
              formatter={(v) => fmtMoney(v)}
              contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {selected.map((id, i) => {
              const s = scenarios.find(x => x.id === id);
              return <Bar key={id} dataKey={s?.name} fill={COLORS[i % COLORS.length]} radius={[4, 4, 0, 0]} />;
            })}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function CompareTab({ scenarios, activeId }) {
  const baseline = scenarios.find(s => s.is_baseline);
  const [selected, setSelected] = useState(() => {
    const init = [];
    if (baseline) init.push(baseline.id);
    if (activeId && activeId !== baseline?.id) init.push(activeId);
    else {
      const other = scenarios.find(s => !s.is_baseline);
      if (other) init.push(other.id);
    }
    return init;
  });
  const [dataById, setDataById] = useState({});
  const [baselineData, setBaselineData] = useState(null);

  const availableToAdd = scenarios.filter(s => !selected.includes(s.id));

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Compare scenarios side-by-side</span>
        {availableToAdd.length > 0 && selected.length < 4 && (
          <Select onValueChange={(v) => setSelected([...selected, v])}>
            <SelectTrigger className="w-[240px]" data-testid="compare-add-select">
              <SelectValue placeholder="+ Add scenario" />
            </SelectTrigger>
            <SelectContent>
              {availableToAdd.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {selected.length === 0 && (
          <span className="text-sm text-muted-foreground">Add scenarios to compare</span>
        )}
      </div>

      <div className={`grid grid-cols-1 md:grid-cols-2 ${selected.length >= 3 ? "lg:grid-cols-3" : ""} ${selected.length >= 4 ? "xl:grid-cols-4" : ""} gap-4`}>
        {selected.map((id, i) => {
          const s = scenarios.find(x => x.id === id);
          if (!s) return null;
          return (
            <ScenarioLoader key={id} id={id}>
              {(data) => {
                if (data && dataById[id] !== data) {
                  // capture data by side-effect via setTimeout to avoid setState during render
                  setTimeout(() => setDataById(prev => (prev[id] === data ? prev : { ...prev, [id]: data })), 0);
                  if (s.is_baseline && baselineData !== data) {
                    setTimeout(() => setBaselineData(data), 0);
                  }
                }
                return (
                  <CompareCard
                    scenario={s}
                    data={data}
                    baseline={s.is_baseline ? null : (baselineData || (baseline && dataById[baseline.id]))}
                    index={i}
                    canRemove={selected.length > 1 && !s.is_baseline}
                    onRemove={() => setSelected(selected.filter(x => x !== id))}
                  />
                );
              }}
            </ScenarioLoader>
          );
        })}
      </div>

      <CompareChart selected={selected} scenarios={scenarios} dataById={dataById} />
    </div>
  );
}
